import {
  BaseResponsePaginationProps,
  BaseResponseProps,
  HeaderSection,
  generateFormRules,
  getStandardError,
} from '@qlibs/react-components';
import {
  Button,
  Card,
  Checkbox,
  DatePicker,
  Divider,
  Form,
  Grid,
  Image,
  Input,
  Row,
  Select,
  Switch,
  Upload,
  message,
} from 'antd';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { backToList } from '../../helpers/listFilterQuery';
import {
  CustomFormItemPer1,
  CustomFormItemPer2,
  CustomFormItemPer3,
} from '../user/components/CustomFormItem';
import useAddress from '../../hooks/useAddress';
import { httpRequest, httpRequestMultipart } from '../../helpers/api';
import {
  AssetProperties,
  EAssetCategory,
  EAssetShipType,
  EAssetType,
  ICreateAsset,
} from './types/asset.type';
import SectionContent from '../../components/SectionContent';
import { CompanyProperties } from '../company/types/company.type';
import { UploadOutlined } from '@ant-design/icons';
import { RcFile } from 'antd/lib/upload';
import dayjs from 'dayjs';
import { handleNewAsset, sendNewAsset } from '../../helpers/handleNewAsset';
import { useAuthUser } from 'react-auth-kit';
import { APP_THEME } from '../../const/config';
import { buildAliasSourceOptions } from './aliasSourceOptions';
import {
  canManageAssetAliasSource,
  canManageTelemetryMqttClient,
} from './assetAliasSourceAccess';
import {
  AssetMobileActionButton,
  AssetMobileField,
  AssetMobileForm,
  AssetMobileHeaderActions,
  AssetMobileImagePreview,
  AssetMobileImageSection,
  AssetMobileNoImagePreview,
  AssetMobilePage,
  AssetMobileSecondaryButton,
  AssetMobileSection,
  AssetMobileSectionHeader,
  AssetMobileSwitchField,
  AssetMobileUploadPanel,
  MobileAssetHeader,
} from './mobile';

const moment = require('moment');

const getLatestFile = (e: any) => {
  return [e?.fileList.pop()];
};

const allowedMimes = ['image/jpeg', 'image/png'];

const AssetCreate = () => {
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const [isLoadingAction, setIsLoadingAction] = React.useState(false);
  const [form] = Form.useForm();
  const [companys, setCompanys] = React.useState<CompanyProperties[]>([]);
  const [sourceAssets, setSourceAssets] = React.useState<AssetProperties[]>([]);
  const [isLoadingSourceAssets, setIsLoadingSourceAssets] =
    React.useState(false);
  const [previewImage, setPreviewImage] = React.useState<
    string | ArrayBuffer | null
  >(null);
  const auth = useAuthUser();
  const canManageAliasSource = canManageAssetAliasSource(auth()?.role);
  const canManageTelemetryMqtt = canManageTelemetryMqttClient(auth()?.role);
  const addAsMqttClient = Form.useWatch('addAsMqttClient', form);

  async function uploadImage(assetId: string, file: File) {
    try {
      let formData = new FormData();
      formData.append('image', file);
      // console.log(file);
      // console.log(formData);
      await httpRequestMultipart.post(
        '/asset/' + assetId + '/upload-image',
        formData
      );
      // message.success(`Success upload asset image`);
    } catch (error) {
      message.error(`Failed upload asset image`);
      throw error;
    } finally {
      setIsLoadingAction(false);
    }
  }

  async function createAsset(values: ICreateAsset) {
    try {
      setIsLoadingAction(true);
      const formData = {
        name: values.name,
        companyId: values.companyId,
        assetType: values.assetType,
        assetCategory: values.assetCategory,
        picName: values.picName,
        picPhoneNumber: values.picPhoneNumber,
        gsmNumber: values.gsmNumber,
        imeiNumber: values.imeiNumber,
        satelliteId: values.satelliteId,
        satelliteDeviceId: values.satelliteDeviceId,
        satelliteOperator: values.satelliteOperator,
        imoNumber: values.imoNumber,
        mmsiNumber: values.mmsiNumber,
        callSign: values.callSign,
        shipType: values.shipType,
        dimensionLength: values.dimensionLength,
        dimensionBeam: values.dimensionBeam,
        dimensionDraught: values.dimensionDraught,
        antennaPosition: values.antennaPosition,
        aliasId: canManageAliasSource ? values.aliasId || null : undefined,
        telemetryMqttClient: canManageTelemetryMqtt
          ? {
              enabled: !!values.addAsMqttClient,
              clientId: values.addAsMqttClient ? values.mqttClientId : null,
              description: values.addAsMqttClient
                ? values.mqttClientDescription
                : null,
            }
          : undefined,
        installationAt: dayjs(values.installationAt),
        timezone: -new Date().getTimezoneOffset() / 60,
        status: values.status ? 'active' : 'inactive',
        metadata: {
          showEventEngine: values.showEventEngine ? 1 : 0,
          showOperationMode: values.showOperationMode ? 1 : 0,
        },
      };

      // console.log(formData);
      // return;

      const response = await httpRequest.post<
        BaseResponseProps<AssetProperties>
      >('/asset', formData);

      if (response.data.payload && values?.image) {
        await uploadImage(
          response.data.payload.assetId,
          (values?.image[0] as any).originFileObj
        );
      }

      message.success(`Success add ${values.name}`);
      form.resetFields();

      const currentCompany = companyMapped.find(
        (item) => item.value === values.companyId
      );

      if (currentCompany) {
        const dataNewAsset = {
          companyName: currentCompany.label || '-',
          assetName: values.name,
          assetType: values.assetType,
          picName: values.picName || '-',
          picPhoneNumber: values.picPhoneNumber || '-',
          createdByUserEmail: auth()?.email || '-',
        };

        if (
          // process.env.REACT_APP_STAGE === 'production' ||
          ['bki', 'netra', 'pinisi'].includes(APP_THEME)
        ) {
          await sendNewAsset(dataNewAsset);
          await handleNewAsset(dataNewAsset);
        }
      }

      backToList(navigate, '/asset');
    } catch (error) {
      getStandardError(error, {
        triggerFormValidation: true,
        formRef: form,
        showToast: true,
      });
    } finally {
      setIsLoadingAction(false);
    }
  }

  const handleSubmit = async (values: any) => {
    // console.log(values);
    await createAsset(values);
  };

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        setIsLoadingAction(true);
        const response = await httpRequest.get<
          BaseResponsePaginationProps<CompanyProperties>
        >('/company');

        if (response.data.payload) {
          setCompanys(response.data.payload.results);
        }
      } catch (error) {
        message.error(`Failed get data companys`);
      } finally {
        setIsLoadingAction(false);
      }
    };

    fetchCompany();
  }, []);

  useEffect(() => {
    if (!canManageAliasSource) {
      return;
    }

    const fetchSourceAssets = async () => {
      try {
        setIsLoadingSourceAssets(true);
        const response = await httpRequest.get<
          BaseResponsePaginationProps<AssetProperties>
        >('/asset', {
          params: {
            limit: 9999,
          },
        });

        if (response.data.payload) {
          setSourceAssets(response.data.payload.results);
        }
      } catch (error) {
        message.error(`Failed get data assets`);
      } finally {
        setIsLoadingSourceAssets(false);
      }
    };

    fetchSourceAssets();
  }, [canManageAliasSource]);

  function handleImagePreview(e: any) {
    const file = e?.file?.originFileObj || e?.file;

    if (file instanceof Blob) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  }

  const companyMapped = React.useMemo(() => {
    return companys.map((item) => ({
      value: item.companyId,
      label: item.name,
    }));
  }, [companys]);

  const assetTypeMapped = React.useMemo(() => {
    return Object.values(EAssetType).map((item) => ({
      value: item,
      label: item,
    }));
  }, []);

  const assetCategoryMapped = React.useMemo(() => {
    return Object.values(EAssetCategory).map((item) => ({
      value: item,
      label: item,
    }));
  }, []);

  const shipTypeMapped = React.useMemo(() => {
    return Object.values(EAssetShipType).map((item) => ({
      value: item,
      label: item,
    }));
  }, []);

  const aliasSourceOptions = React.useMemo(() => {
    return buildAliasSourceOptions(sourceAssets);
  }, [sourceAssets]);

  function handleBeforeUpload(file: RcFile) {
    let max = 2;
    const isImageAllowed = ['image/jpeg', 'image/jpg', 'image/png'].includes(
      file.type
    );
    const isLimitSize = file.size / 1024 / 1024 < max;

    if (!isImageAllowed) {
      message.error('You can only upload JPG, JPEG, and PNG file!');
    }

    if (!isLimitSize) {
      message.error(`Image must smaller than ${max}MB!`);
    }

    return false;
  }

  const renderMobileContent = () => (
    <AssetMobilePage>
      <MobileAssetHeader
        title="Asset"
        onBack={() => backToList(navigate, '/asset')}
        action={
          <AssetMobileHeaderActions>
            <AssetMobileSecondaryButton onClick={() => backToList(navigate, '/asset')}>
              Cancel
            </AssetMobileSecondaryButton>
            <AssetMobileActionButton
              type="primary"
              loading={isLoadingAction}
              onClick={() => form.submit()}
            >
              Save
            </AssetMobileActionButton>
          </AssetMobileHeaderActions>
        }
      />

      <AssetMobileForm>
        <Form
          form={form}
          name="profileForm"
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <AssetMobileSection $bordered>
            <AssetMobileSectionHeader>
              <h2>Information</h2>
              <p>Detail information about asset</p>
            </AssetMobileSectionHeader>
            <AssetMobileField label="Image">
              <AssetMobileImageSection>
                {previewImage ? (
                  <AssetMobileImagePreview
                    src={previewImage as any}
                    fallback="/images/empty-image.png"
                    preview={false}
                  />
                ) : (
                  <AssetMobileNoImagePreview />
                )}
                <Form.Item
                  name="image"
                  valuePropName="fileList"
                  getValueFromEvent={getLatestFile}
                  noStyle
                >
                  <AssetMobileUploadPanel
                    maxCount={1}
                    accept={allowedMimes.join(',')}
                    beforeUpload={handleBeforeUpload}
                    multiple={false}
                    action={undefined}
                    showUploadList={false}
                    onChange={handleImagePreview}
                  >
                    <Button icon={<UploadOutlined />}>Click to upload</Button>
                    <span className="upload-info">
                      Allowed type are JPEG, JPG and PNG. Max size are 2 Mb.
                      Recommended size are 1000x1000 pixel
                    </span>
                  </AssetMobileUploadPanel>
                </Form.Item>
              </AssetMobileImageSection>
            </AssetMobileField>
            <AssetMobileField
              label="Asset Name"
              name="name"
              rules={generateFormRules('Asset Name', ['required'])}
            >
              <Input placeholder="Input asset name" />
            </AssetMobileField>
            <AssetMobileField
              label="Company"
              name="companyId"
              rules={generateFormRules('Company', ['required'])}
            >
              <Select
                showSearch
                placeholder="Choose company"
                style={{ width: '100%' }}
                options={companyMapped}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </AssetMobileField>
            {canManageAliasSource ? (
              <AssetMobileField label="Clone Devices From" name="aliasId">
                <Select
                  allowClear
                  showSearch
                  disabled={isLoadingSourceAssets}
                  loading={isLoadingSourceAssets}
                  placeholder="Choose source asset"
                  style={{ width: '100%' }}
                  options={aliasSourceOptions}
                  optionFilterProp="searchLabel"
                />
              </AssetMobileField>
            ) : null}
            {canManageTelemetryMqtt ? (
              <>
                <AssetMobileSwitchField
                  label="Add as mqtt client"
                  name="addAsMqttClient"
                  valuePropName="checked"
                >
                  <Switch />
                </AssetMobileSwitchField>
                {addAsMqttClient ? (
                  <>
                    <AssetMobileField
                      label="MQTT client Id"
                      name="mqttClientId"
                      rules={generateFormRules('MQTT client Id', ['required'])}
                    >
                      <Input placeholder="Input MQTT client Id" />
                    </AssetMobileField>
                    <AssetMobileField
                      label="MQTT client description"
                      name="mqttClientDescription"
                    >
                      <Input.TextArea
                        placeholder="Input MQTT client description"
                        rows={3}
                      />
                    </AssetMobileField>
                  </>
                ) : null}
              </>
            ) : null}
            <AssetMobileField
              label="Asset Type"
              name="assetType"
              rules={generateFormRules('Asset Type', ['required'])}
            >
              <Select
                showSearch
                placeholder="Choose asset type"
                style={{ width: '100%' }}
                options={assetTypeMapped}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </AssetMobileField>
            <AssetMobileField
              label="Asset Category"
              name="assetCategory"
              rules={generateFormRules('Asset Category', ['required'])}
            >
              <Select
                showSearch
                placeholder="Choose asset category"
                style={{ width: '100%' }}
                options={assetCategoryMapped}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </AssetMobileField>
            <AssetMobileField label="Ship Type" name="shipType">
              <Select
                showSearch
                placeholder="Choose ship type"
                style={{ width: '100%' }}
                options={shipTypeMapped}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </AssetMobileField>
          </AssetMobileSection>

          <AssetMobileSection $bordered>
            <AssetMobileSectionHeader>
              <h2>Informational Data</h2>
              <p>These are asset information, you can change anything</p>
            </AssetMobileSectionHeader>
            <AssetMobileField
              label="PIC Name"
              name="picName"
              rules={generateFormRules('PIC name', ['letter-and-space'])}
            >
              <Input placeholder="Input pic name" />
            </AssetMobileField>
            <AssetMobileField
              label="PIC Phone Number"
              name="picPhoneNumber"
              rules={generateFormRules('PIC phone number', ['phoneNumber'])}
            >
              <Input placeholder="Input pic phone number" />
            </AssetMobileField>
            <AssetMobileField
              label="IMO Number"
              name="imoNumber"
              rules={generateFormRules('IMO Number', ['numeric'])}
            >
              <Input placeholder="Input imo number" />
            </AssetMobileField>
            <AssetMobileField
              label="MMSI Number"
              name="mmsiNumber"
              rules={generateFormRules('MMSI Number', ['numeric'])}
            >
              <Input placeholder="Input mmsi number" />
            </AssetMobileField>
            <AssetMobileField label="Call Sign" name="callSign">
              <Input placeholder="Input call sign" />
            </AssetMobileField>
            <AssetMobileField label="Antenna Position" name="antennaPosition">
              <Input placeholder="Input antenna position" />
            </AssetMobileField>
            <AssetMobileField
              label="Dimension Length"
              name="dimensionLength"
              rules={generateFormRules('Dimension Length', ['numeric'])}
            >
              <Input placeholder="Input dimension length" suffix="Meters" />
            </AssetMobileField>
            <AssetMobileField
              label="Dimension Beam"
              name="dimensionBeam"
              rules={generateFormRules('Dimension Beam', ['numeric'])}
            >
              <Input placeholder="Input dimension beam" suffix="Meters" />
            </AssetMobileField>
            <AssetMobileField
              label="Dimension Draught"
              name="dimensionDraught"
              rules={generateFormRules('Dimension Draught', ['numeric'])}
            >
              <Input placeholder="Input dimension draught" suffix="Meters" />
            </AssetMobileField>
          </AssetMobileSection>

          <AssetMobileSection $bordered>
            <AssetMobileSectionHeader>
              <h2>Satellite & ID</h2>
              <p>These are asset information, you can change anything</p>
            </AssetMobileSectionHeader>
            <AssetMobileField
              label="GSM Number"
              name="gsmNumber"
              rules={generateFormRules('GSM Number', ['numeric'])}
            >
              <Input placeholder="Input gsm number" />
            </AssetMobileField>
            <AssetMobileField
              label="IMEI Number"
              name="imeiNumber"
              rules={generateFormRules('Imei Number', ['numeric'])}
            >
              <Input placeholder="Input imei number" />
            </AssetMobileField>
            <AssetMobileField
              label="Satellite ID"
              name="satelliteId"
              rules={generateFormRules('Satellite ID', ['numeric'])}
            >
              <Input placeholder="Input satellite id" />
            </AssetMobileField>
            <AssetMobileField
              label="Satellite Device ID"
              name="satelliteDeviceId"
              rules={generateFormRules('Satellite Device ID', ['numeric'])}
            >
              <Input placeholder="Input satellite device id" />
            </AssetMobileField>
            <AssetMobileField
              label="Satellite Operator"
              name="satelliteOperator"
              rules={generateFormRules('Satellite Operator', [
                'letter-and-space',
              ])}
            >
              <Input placeholder="Input satellite operator" />
            </AssetMobileField>
            <AssetMobileField label="Installation Date" name="installationAt">
              <DatePicker
                style={{ width: '100%', height: 44, borderRadius: 6 }}
              />
            </AssetMobileField>
          </AssetMobileSection>

          <AssetMobileSection $bordered>
            <AssetMobileSectionHeader>
              <h2>Setting</h2>
              <p>These are asset information, you can change anything</p>
            </AssetMobileSectionHeader>
            <AssetMobileSwitchField
              label="Status"
              name="status"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch defaultChecked className="custom-switch" />
            </AssetMobileSwitchField>
          </AssetMobileSection>

          <AssetMobileSection>
            <AssetMobileSectionHeader>
              <h2>Metadata</h2>
              <p>Feature flags for this asset</p>
            </AssetMobileSectionHeader>
            <AssetMobileField
              name="showOperationMode"
              valuePropName="checked"
              initialValue={false}
            >
              <Checkbox>Show Operation Mode Tab</Checkbox>
            </AssetMobileField>
            <AssetMobileField
              name="showEventEngine"
              valuePropName="checked"
              initialValue={false}
            >
              <Checkbox>Show Event Engine Tab</Checkbox>
            </AssetMobileField>
          </AssetMobileSection>
        </Form>
      </AssetMobileForm>
    </AssetMobilePage>
  );

  if (isMobile) {
    return renderMobileContent();
  }

  return (
    <React.Fragment>
      <HeaderSection
        icon='back'
        title={'Add Asset'}
        subtitle={'Create new asset'}
        showCancelButton
        showSaveButton
        isLoading={isLoadingAction}
        onSave={() => form.submit()}
      />

      <Card>
        <Form
          form={form}
          name='profileForm'
          layout='vertical'
          onFinish={handleSubmit}
          autoComplete='off'
        >
          <SectionContent
            groupTitle='Information'
            subTitle='Detail information about asset'
          >
            <CustomFormItemPer1
              label='Image'
              name='imagePreview'
              valuePropName='fileList'
              getValueFromEvent={getLatestFile}
              style={{ marginBottom: 0 }}
            >
              <Image
                fallback='/images/empty-image.png'
                src={previewImage as any}
                width={'100%'}
                height={300}
                style={{
                  objectFit: 'contain',
                  backgroundColor: '#f4f6f9',
                  borderRadius: 8,
                  marginBottom: 40,
                  display: 'block',
                }}
              />
            </CustomFormItemPer1>
            <CustomFormItemPer1
              name='image'
              valuePropName='fileList'
              getValueFromEvent={getLatestFile}
            >
              <Upload
                maxCount={1}
                accept={allowedMimes.join(',')}
                beforeUpload={handleBeforeUpload}
                multiple={false}
                showUploadList={false}
                action={undefined}
                name='image'
                onChange={(e) => {
                  handleImagePreview(e);
                }}
                className='upload-form'
              >
                <Button icon={<UploadOutlined />}>Click to upload</Button>
                <span className='upload-info'>
                  Allowed type are JPEG, JPG and PNG. Max size are 2 Mb.
                  Recommended size are 1000x1000 pixel
                </span>
              </Upload>
            </CustomFormItemPer1>
            <CustomFormItemPer1
              label='Asset Name'
              name='name'
              rules={generateFormRules('Asset Name', ['required'])}
            >
              <Input placeholder='Input asset name' />
            </CustomFormItemPer1>
            <CustomFormItemPer1
              label='Company'
              name='companyId'
              rules={generateFormRules('Company', ['required'])}
            >
              <Select
                showSearch
                placeholder='Choose company'
                style={{ width: '100%' }}
                options={companyMapped}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </CustomFormItemPer1>
            {canManageAliasSource ? (
              <CustomFormItemPer1
                label='Clone Devices From'
                name='aliasId'
              >
                <Select
                  allowClear
                  showSearch
                  disabled={isLoadingSourceAssets}
                  loading={isLoadingSourceAssets}
                  placeholder='Choose source asset'
                  style={{ width: '100%' }}
                  options={aliasSourceOptions}
                  optionFilterProp='searchLabel'
                />
              </CustomFormItemPer1>
            ) : null}
            {canManageTelemetryMqtt ? (
              <>
                <CustomFormItemPer1
                  label='Add as mqtt client'
                  name='addAsMqttClient'
                  valuePropName='checked'
                >
                  <Switch />
                </CustomFormItemPer1>
                {addAsMqttClient ? (
                  <>
                    <CustomFormItemPer1
                      label='MQTT client Id'
                      name='mqttClientId'
                      rules={generateFormRules('MQTT client Id', ['required'])}
                    >
                      <Input placeholder='Input MQTT client Id' />
                    </CustomFormItemPer1>
                    <CustomFormItemPer1
                      label='MQTT client description'
                      name='mqttClientDescription'
                    >
                      <Input.TextArea
                        placeholder='Input MQTT client description'
                        rows={3}
                      />
                    </CustomFormItemPer1>
                  </>
                ) : null}
              </>
            ) : null}
            <Row>
              <CustomFormItemPer3
                label='Asset Type'
                name='assetType'
                rules={generateFormRules('Asset Type', ['required'])}
              >
                <Select
                  showSearch
                  placeholder='Choose asset type'
                  style={{ width: '100%' }}
                  options={assetTypeMapped}
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </CustomFormItemPer3>
              <CustomFormItemPer3
                label='Asset Category'
                name='assetCategory'
                rules={generateFormRules('Asset Category', ['required'])}
              >
                <Select
                  showSearch
                  placeholder='Choose asset category'
                  style={{ width: '100%' }}
                  options={assetCategoryMapped}
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </CustomFormItemPer3>
              <CustomFormItemPer3 label='Ship Type' name='shipType'>
                <Select
                  showSearch
                  placeholder='Choose ship type'
                  style={{ width: '100%' }}
                  options={shipTypeMapped}
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </CustomFormItemPer3>
            </Row>
          </SectionContent>

          <Divider />

          <SectionContent
            groupTitle={`Information`}
            subTitle='These are asset information, you can change anything'
          >
            <Row>
              <CustomFormItemPer2
                label='PIC Name'
                name='picName'
                rules={generateFormRules('PIC name', ['letter-and-space'])}
              >
                <Input placeholder='Input pic name' />
              </CustomFormItemPer2>
              <CustomFormItemPer2
                label='PIC Phone Number'
                name='picPhoneNumber'
                rules={generateFormRules('PIC phone number', ['phoneNumber'])}
              >
                <Input placeholder='Input pic phone number' />
              </CustomFormItemPer2>
            </Row>
            <Row>
              <CustomFormItemPer2
                label='IMO Number'
                name='imoNumber'
                rules={generateFormRules('IMO Number', ['numeric'])}
              >
                <Input placeholder='Input imo number' />
              </CustomFormItemPer2>
              <CustomFormItemPer2
                label='MMSI Number'
                name='mmsiNumber'
                rules={generateFormRules('MMSI Number', ['numeric'])}
              >
                <Input placeholder='Input mmsi number' />
              </CustomFormItemPer2>
            </Row>
            <Row>
              <CustomFormItemPer2 label='Call Sign' name='callSign'>
                <Input placeholder='Input call sign' />
              </CustomFormItemPer2>
              <CustomFormItemPer2
                label='Antenna Position'
                name='antennaPosition'
              >
                <Input placeholder='Input antenna position' />
              </CustomFormItemPer2>
            </Row>
            <Row>
              <CustomFormItemPer3
                label='Dimension Length'
                name='dimensionLength'
                rules={generateFormRules('Dimension Length', ['numeric'])}
              >
                <Input placeholder='Input dimension length' suffix='Meters' />
              </CustomFormItemPer3>
              <CustomFormItemPer3
                label='Dimension Beam'
                name='dimensionBeam'
                rules={generateFormRules('Dimension Beam', ['numeric'])}
              >
                <Input placeholder='Input dimension beam' suffix='Meters' />
              </CustomFormItemPer3>
              <CustomFormItemPer3
                label='Dimension Draught'
                name='dimensionDraught'
                rules={generateFormRules('Dimension Draught', ['numeric'])}
              >
                <Input placeholder='Input dimension draught' suffix='Meters' />
              </CustomFormItemPer3>
            </Row>
          </SectionContent>

          <Divider />

          <SectionContent
            groupTitle={`Satellite & ID`}
            subTitle='These are asset information, you can change anything'
          >
            <Row>
              <CustomFormItemPer2
                label='GSM Number'
                name='gsmNumber'
                rules={generateFormRules('GSM Number', ['numeric'])}
              >
                <Input placeholder='Input gsm number' />
              </CustomFormItemPer2>
              <CustomFormItemPer2
                label='IMEI Number'
                name='imeiNumber'
                rules={generateFormRules('Imei Number', ['numeric'])}
              >
                <Input placeholder='Input imei number' />
              </CustomFormItemPer2>
            </Row>
            <Row>
              <CustomFormItemPer3
                label='Satellite ID'
                name='satelliteId'
                rules={generateFormRules('Satellite ID', ['numeric'])}
              >
                <Input placeholder='Input satellite id' />
              </CustomFormItemPer3>
              <CustomFormItemPer3
                label='Satellite Device ID'
                name='satelliteDeviceId'
                rules={generateFormRules('Satellite Device ID', ['numeric'])}
              >
                <Input placeholder='Input satellite device id' />
              </CustomFormItemPer3>
              <CustomFormItemPer3
                label='Satellite Operator'
                name='satelliteOperator'
                rules={generateFormRules('Satellite Operator', [
                  'letter-and-space',
                ])}
              >
                <Input placeholder='Input satellite operator' />
              </CustomFormItemPer3>
            </Row>
            <Row>
              <CustomFormItemPer3
                label='Installation Date'
                name='installationAt'
              >
                <DatePicker style={{ width: '100%' }} />
              </CustomFormItemPer3>
            </Row>
          </SectionContent>

          <Divider />

          <SectionContent
            groupTitle={`Setting`}
            subTitle='These are asset information, you can change anything'
          >
            <CustomFormItemPer1
              label='Status'
              name='status'
              valuePropName='checked'
              initialValue={true}
            >
              <Switch defaultChecked className='custom-switch' />
            </CustomFormItemPer1>
          </SectionContent>

          <Divider />

          <SectionContent
            groupTitle={`Metadata`}
            subTitle='Feature flags for this asset'
          >
            <CustomFormItemPer1
              name='showOperationMode'
              valuePropName='checked'
              initialValue={false}
            >
              <Checkbox>Show Operation Mode Tab</Checkbox>
            </CustomFormItemPer1>
            <CustomFormItemPer1
              name='showEventEngine'
              valuePropName='checked'
              initialValue={false}
            >
              <Checkbox>Show Event Engine Tab</Checkbox>
            </CustomFormItemPer1>
          </SectionContent>
        </Form>
      </Card>
    </React.Fragment>
  );
};

export default AssetCreate;
